"use client";

import { useMemo } from "react";
import { CodeBlock } from "./CodeBlock";
import { FaqAccordion, type FaqItemData } from "./FaqAccordion";

interface ArticleContentParserProps {
  html: string;
  slug: string;
}

type ParsedSection =
  | { type: "html"; content: string }
  | { type: "code"; code: string; index: number }
  | { type: "faq"; title: string; items: FaqItemData[] };

export function ArticleContentParser({ html, slug }: ArticleContentParserProps) {
  const parsedSections = useMemo(() => {
    if (!html) return [];

    const sections: ParsedSection[] = [];
    let remainingHtml = html;

    // 1. Check if FAQ section exists and separate it
    let faqSectionData: { title: string; items: FaqItemData[] } | null = null;
    const faqMatch = remainingHtml.match(/<h2>(Perguntas Frequentes[\s\S]*?)<\/h2>/i);

    if (faqMatch) {
      const faqHeaderTitle = faqMatch[1].replace(/<[^>]*>/g, "").trim();
      const splitParts = remainingHtml.split(faqMatch[0]);

      const mainBodyBeforeFaq = splitParts[0];
      const afterFaqHeader = splitParts.slice(1).join(faqMatch[0]);

      // Parse H3 and P inside FAQ portion
      const faqItems: FaqItemData[] = [];
      const h3Regex = /<h3>(.*?)<\/h3>([\s\S]*?)(?=(?:<h3>|<h2>|$))/gi;
      let h3Match;

      while ((h3Match = h3Regex.exec(afterFaqHeader)) !== null) {
        const question = h3Match[1].replace(/<[^>]*>/g, "").trim();
        const answerHtml = h3Match[2].trim();
        if (question && answerHtml) {
          faqItems.push({ question, answerHtml });
        }
      }

      if (faqItems.length > 0) {
        faqSectionData = { title: faqHeaderTitle, items: faqItems };
        // Truncate remaining HTML before FAQ header so it doesn't duplicate
        remainingHtml = mainBodyBeforeFaq;
      }
    }

    // 2. Parse Code Blocks <pre><code...>...</code></pre>
    const codeBlockRegex = /<pre><code.*?>([\s\S]*?)<\/code><\/pre>/gi;
    let lastIdx = 0;
    let match;
    let codeIndex = 1;

    while ((match = codeBlockRegex.exec(remainingHtml)) !== null) {
      const textBefore = remainingHtml.substring(lastIdx, match.index);
      if (textBefore.trim()) {
        sections.push({ type: "html", content: textBefore });
      }

      // Decode HTML entities in code snippet
      const rawCode = match[1]
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'");

      sections.push({ type: "code", code: rawCode, index: codeIndex++ });
      lastIdx = match.index + match[0].length;
    }

    const textAfter = remainingHtml.substring(lastIdx);
    if (textAfter.trim()) {
      sections.push({ type: "html", content: textAfter });
    }

    // 3. Append FAQ Accordion Section at the end if present
    if (faqSectionData) {
      sections.push({ type: "faq", title: faqSectionData.title, items: faqSectionData.items });
    }

    return sections;
  }, [html]);

  return (
    <div className="article-parsed-content">
      {parsedSections.map((section, idx) => {
        if (section.type === "html") {
          return (
            <div
              key={idx}
              className="prose"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          );
        }

        if (section.type === "code") {
          return <CodeBlock key={idx} code={section.code} slug={slug} index={section.index} />;
        }

        if (section.type === "faq") {
          return <FaqAccordion key={idx} title={section.title} items={section.items} />;
        }

        return null;
      })}
    </div>
  );
}
